import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { NgComponentOutlet, NgTemplateOutlet } from '@angular/common';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideCheck, lucideTriangleAlert, lucideX } from '@ng-icons/lucide';
import { HlmButton } from '@lite-workspace/shared/helm-ui/button';
import { HlmSpinner } from '@lite-workspace/shared/helm-ui/spinner';
import { ToastService, type Toast, type ToastKind } from './toast.service';

// Выше CDK-оверлея (z-index 1000): отказ мутации часто прилетает, когда диалог ещё открыт —
// под его скримом тост размывается и гаснет, то есть доклада нет вовсе.
const COLUMN = 'pointer-events-none fixed z-[var(--z-toast)] flex flex-col gap-3';
const CARD_RIGHT = 'right-6 items-end';
const BOTTOM_RIGHT = `${CARD_RIGHT} bottom-[var(--bottom-toast)] pointer-coarse:bottom-[var(--bottom-toast-coarse)]`;
// A fixed column with `right` set moves left by its margin: a right-edge rail (lightbox «Кто это?»)
// publishes its width on <html> and the card column keeps clear of it.
const INSET_RIGHT = 'var(--toast-inset-right, 0px)';

@Component({
  selector: 'hlm-toast-host',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: COLUMN,
    '[class]': 'placementClass()',
    '[style.top]': 'topCss()',
    '[style.margin-right]': 'phone() ? null : insetRight',
  },
  imports: [NgIcon, HlmButton, HlmSpinner, NgTemplateOutlet, NgComponentOutlet],
  viewProviders: [provideIcons({ lucideCheck, lucideTriangleAlert, lucideX })],
  template: `
    @for (t of mainToasts(); track t.id) {
      <ng-container *ngTemplateOutlet="card; context: { $implicit: t }" />
    }
    @if (bottomToasts().length) {
      <div [class]="bottomColumnClass" [style.margin-right]="insetRight">
        @for (t of bottomToasts(); track t.id) {
          <ng-container *ngTemplateOutlet="card; context: { $implicit: t }" />
        }
      </div>
    }
    <ng-template #card let-t>
      <div
        role="status"
        aria-live="polite"
        (click)="onCardClick(t)"
        [class.cursor-pointer]="!t.actionLabel && !t.secondaryActionLabel"
        [class]="cardClass(t.kind)"
        class="pointer-events-auto flex items-start gap-3 border px-4 py-3 shadow-[var(--shadow-3)]"
      >
        @if (t.kind === 'loading') {
          <hlm-spinner class="mt-0.5 shrink-0 text-c2" [size]="16" />
        } @else if (kindIcon(t.kind); as icon) {
          <ng-icon [name]="icon" [class]="iconClass(t.kind)" class="mt-0.5 text-lg leading-none" />
        }
        <div class="flex-1">
          <!-- Перенос строки в тексте тоста рисуется строкой: «что случилось» сверху, «что делать» снизу. -->
          @if (t.content; as c) {
            <div class="flex items-center gap-2">
              <p [class]="textClass(t.kind)" class="font-medium">{{ t.title }}</p>
              <ng-container *ngComponentOutlet="c.component; inputs: c.inputs" />
            </div>
          } @else {
            <p [class]="textClass(t.kind)" class="font-medium">{{ t.title }}</p>
          }
          @if (t.items?.length) {
            <ul [class]="detailClass(t.kind)" class="mt-1 mb-1.5 list-disc space-y-0.5 pl-5">
              @for (item of t.items; track $index) {
                <li>{{ item }}</li>
              }
            </ul>
          }
          @if (t.detail) {
            <p [class]="detailClass(t.kind)" class="mt-0.5">{{ t.detail }}</p>
          }
          <!-- Два ответа — кнопками под текстом, текст во всю ширину тоста: справа от него они сжали бы его в столбик. -->
          @if (t.secondaryActionLabel) {
            <div class="mt-3 flex flex-wrap gap-2">
              @if (t.actionLabel) {
                <button hlmBtn size="sm" type="button" (click)="runAction(t.id, $event)">{{ t.actionLabel }}</button>
              }
              @if (onMenuPlate(t.kind)) {
                <button hlmBtn variant="on-menu-outline" size="sm" type="button" (click)="runSecondaryAction(t.id, $event)">
                  {{ t.secondaryActionLabel }}
                </button>
              } @else {
                <button hlmBtn variant="outline" size="sm" type="button" (click)="runSecondaryAction(t.id, $event)">
                  {{ t.secondaryActionLabel }}
                </button>
              }
            </div>
          }
        </div>
        @if (t.actionLabel && !t.secondaryActionLabel) {
          <button
            type="button"
            (click)="runAction(t.id, $event)"
            class="ml-1 shrink-0 self-center rounded-[var(--r-s)] text-xs font-semibold text-acc outline-none
              hover:underline focus-visible:ring-1 focus-visible:ring-ring"
          >
            {{ t.actionLabel }}
          </button>
          @if (t.closable) {
            <button
              type="button"
              (click)="close(t.id, $event)"
              aria-label="Закрыть"
              class="ml-1 self-center rounded-[var(--r-s)] text-xs text-c3 outline-none hover:text-ink
                focus-visible:ring-1 focus-visible:ring-ring
                pointer-coarse:inline-flex pointer-coarse:size-11 pointer-coarse:items-center pointer-coarse:justify-center"
            >
              <ng-icon name="lucideX" />
            </button>
          }
        } @else if (t.closable !== false) {
          <!-- У тоста с выбором крестик в углу, у строки заголовка: кнопки ответов — под текстом. -->
          <button
            type="button"
            (click)="close(t.id, $event)"
            aria-label="Закрыть"
            [class.self-center]="!t.secondaryActionLabel"
            [class.self-start]="t.secondaryActionLabel"
            [class.mt-1]="t.secondaryActionLabel"
            class="ml-1 rounded-[var(--r-s)] text-xs text-c3 outline-none hover:text-ink
              focus-visible:ring-1 focus-visible:ring-ring
              pointer-coarse:inline-flex pointer-coarse:size-11 pointer-coarse:items-center pointer-coarse:justify-center"
          >
            <ng-icon name="lucideX" />
          </button>
        }
      </div>
    </ng-template>
  `,
})
export class ToastHostComponent {
  protected readonly toast = inject(ToastService);

  /** Верх берёт только приложение, которое публикует высоту своей шапки (`--app-shell-header-h`). */
  readonly position = input<'top' | 'bottom'>('bottom');

  /** Отступ тоста от верха, px. */
  readonly offsetTop = input(16);

  /** Телефонная форма: полоса во всю ширину без скруглений, сторона — верх. Включает хост приложения
   *  (fotoclass-app передаёт свой `IS_MOBILE`); хост без входа остаётся карточкой, как прежде. */
  readonly phone = input(false);

  /** В телефонной форме сторона всегда верх: низ экрана занимают выдвижные листы и панели, и тост под ними
   *  оставался бы непрочитанным. Без неё сторону задаёт `position`. */
  protected readonly resolvedPosition = computed<'top' | 'bottom'>(() => (this.phone() ? 'top' : this.position()));

  /** Тост садится ПОД шапку приложения: её высоту публикует hlm-app-shell (`--app-shell-header-h`), а корень
   *  приложения повторяет её рядом с роутер-аутлетом; приложение без шапки живёт на дефолте 0 и получает голый отступ. */
  protected readonly topCss = computed(() =>
    this.resolvedPosition() === 'top' ? `calc(var(--app-shell-header-h, 0px) + ${this.offsetTop()}px)` : null,
  );

  /**
   * Телефонная форма (вход `phone`) — полоса во всю ширину под шапкой. Иначе тост остаётся карточкой у
   * правого края — и на планшете тоже: полоса с полями на грубом указателе не выходила (карточка держит
   * `max-w-md`), а без `items-end` она прилипала к левому краю. Крестик 44×44 на грубом указателе — у кнопки.
   * Низ — над треем отбора, когда он на экране (`--wz-tray-h` публикует сам трей); без трея — обычные 24 px,
   * а на грубом указателе ещё и над домашней чертой (`env(safe-area-inset-bottom)`).
   */
  protected readonly placementClass = computed(() => {
    if (this.phone()) return 'left-0 right-0';
    return this.resolvedPosition() === 'bottom' ? BOTTOM_RIGHT : CARD_RIGHT;
  });

  /** A top host lifts explicit `position: 'bottom'` toasts into a bottom-right column; phone and bottom hosts keep one column. */
  private readonly splitBottom = computed(() => !this.phone() && this.resolvedPosition() === 'top');

  protected readonly mainToasts = computed(() =>
    this.splitBottom() ? this.toast.toasts().filter(t => t.position !== 'bottom') : this.toast.toasts(),
  );

  protected readonly bottomToasts = computed(() =>
    this.splitBottom() ? this.toast.toasts().filter(t => t.position === 'bottom') : [],
  );

  protected readonly bottomColumnClass = `${COLUMN} ${BOTTOM_RIGHT}`;

  protected readonly insetRight = INSET_RIGHT;

  protected iconClass(kind: ToastKind): string {
    if (kind === 'success') return 'text-ok';
    if (kind === 'error') return 'text-destructive';
    if (kind === 'warn') return 'text-warn';
    return 'text-c2';
  }

  /**
   * Отказ и предупреждение носят свой цвет ПЛАШКОЙ, а не одной иконкой: сообщение приходит поверх
   * работы, и нейтральная карточка на краю экрана проходит мимо глаза. Успех и подтверждение остаются
   * нейтральными — им кричать не о чем.
   *
   * Отказ и предупреждение при этом непрозрачны: они прилетают поверх холста и ленты разворотов, а
   * `--warn-soft`/`--bad-soft` в тёмной теме — это тон с прозрачностью 0.15, сквозь который просвечивала
   * страница и текст переставал читаться вовсе. Тёмная плашка держит контраст на любом фоне, а цветом
   * кричат рамка и знак внимания.
   */
  protected cardClass(kind: ToastKind): string {
    // Телефон: полоса во всю ширину без скруглений, десктоп: карточка прежней ширины.
    const shape = this.phone() ? 'max-w-none rounded-none' : 'max-w-md rounded-xl';
    if (kind === 'error') return `${shape} border-destructive-line bg-menu-surface`;
    if (kind === 'warn') return `${shape} border-warn bg-menu-surface`;
    return `${shape} border-line2 bg-bg2`;
  }

  /** Отказ читается на строку крупнее: его читают, отвлекаясь от кадра в руке. Цвет — от своей плашки. */
  protected textClass(kind: ToastKind): string {
    const wrap = this.wrapClass(kind);
    if (kind === 'error' || kind === 'warn') return `${wrap} text-base text-on-menu-surface`;
    return `${wrap} text-sm text-ink`;
  }

  /** Перенос строки рисуется всюду: вторая строка отказа — это «что делать», без неё доклада нет, поэтому
   *  отказ и предупреждение не обрезаются никогда. Успех и справка в телефонной форме держат одну строку. */
  private wrapClass(kind: ToastKind): string {
    const clamp = this.phone() && (kind === 'success' || kind === 'info');
    return clamp ? 'whitespace-pre-line line-clamp-1' : 'whitespace-pre-line';
  }

  /** Вторая строка — на ступень мельче заголовка и без его насыщенности; цвет остаётся цветом своей плашки,
   *  чтобы объяснение читалось и на тёмной плашке отказа. */
  protected detailClass(kind: ToastKind): string {
    const wrap = this.wrapClass(kind);
    if (kind === 'error' || kind === 'warn') return `${wrap} text-sm text-on-menu-surface`;
    return `${wrap} text-xs text-c2`;
  }

  /** Отказ и предупреждение лежат на тёмной плите меню: второй ответ берёт её контур, а не контур панели. */
  protected onMenuPlate(kind: ToastKind): boolean {
    return kind === 'error' || kind === 'warn';
  }

  protected kindIcon(kind: ToastKind): string {
    if (kind === 'success') return 'lucideCheck';
    // Знак внимания, а не крестик: крестик рядом с кнопкой закрытия читается второй кнопкой.
    if (kind === 'error' || kind === 'warn') return 'lucideTriangleAlert';
    return '';
  }

  protected onCardClick(toast: Toast): void {
    if (!toast.actionLabel && !toast.secondaryActionLabel) this.toast.dismiss(toast.id);
  }

  protected runAction(id: number, event: Event): void {
    event.stopPropagation();
    this.toast.runAction(id);
  }

  protected runSecondaryAction(id: number, event: Event): void {
    event.stopPropagation();
    this.toast.runSecondaryAction(id);
  }

  protected close(id: number, event: Event): void {
    event.stopPropagation();
    this.toast.dismiss(id);
  }
}
